/**
 * Deal report CSV data generation utilities
 */
import { format } from 'date-fns'

import type {
  ListOrganizationDealPerFrameResponse_DealPerFrame as DealPerFrame,
  ListOrganizationDealPerFrameResponse,
} from '@enechain/eposition-proto/bffportal/v1/report_service_pb'
import { AreaCode } from '@enechain/eposition-proto/bfftypes/v1/area_pb'
import { DealPosition } from '@enechain/eposition-proto/bfftypes/v1/deal_pb'
import { generateCsvString } from '@enechain/eposition-utils'

import { generateContractExportFileName } from '~/pages/ContractList/utils/contractExportFileNameGenerator'

import {
  CSV_COLUMN_ORDER,
  type DealReportCsvRow,
  FRAME_CONFIG,
} from '../types/types'

/**
 * Convert API response to deal report CSV rows
 */
export function generatePerFrameContractCsvRows(
  response: ListOrganizationDealPerFrameResponse,
  fromDate: Date,
  toDate: Date,
): DealReportCsvRow[] {
  const { dates: allDates, dataMap } = createDatesAndLookupMap(
    response.dealsPerFrame ?? [],
    fromDate,
    toDate,
  )

  const allRows: DealReportCsvRow[] = []

  for (const date of allDates) {
    const dateKey = format(date, 'yyyy-MM-dd')

    for (
      let frame = FRAME_CONFIG.FRAME_START;
      frame <= FRAME_CONFIG.TOTAL_FRAMES;
      frame++
    ) {
      const row: DealReportCsvRow = {
        date: dateKey,
        frame,
        timeFrom: formatFrameStartTime(frame),
        timeTo: formatFrameEndTime(frame),
        // Get kWh values directly from lookup map
        hokkaidoBid: getKwhValue(
          dataMap,
          dateKey,
          frame,
          AreaCode.HOKKAIDO,
          DealPosition.BID,
        ),
        hokkaidoOffer: getKwhValue(
          dataMap,
          dateKey,
          frame,
          AreaCode.HOKKAIDO,
          DealPosition.OFFER,
        ),
        tohokuBid: getKwhValue(
          dataMap,
          dateKey,
          frame,
          AreaCode.TOHOKU,
          DealPosition.BID,
        ),
        tohokuOffer: getKwhValue(
          dataMap,
          dateKey,
          frame,
          AreaCode.TOHOKU,
          DealPosition.OFFER,
        ),
        tokyoBid: getKwhValue(
          dataMap,
          dateKey,
          frame,
          AreaCode.TOKYO,
          DealPosition.BID,
        ),
        tokyoOffer: getKwhValue(
          dataMap,
          dateKey,
          frame,
          AreaCode.TOKYO,
          DealPosition.OFFER,
        ),
        chubuBid: getKwhValue(
          dataMap,
          dateKey,
          frame,
          AreaCode.CHUBU,
          DealPosition.BID,
        ),
        chubuOffer: getKwhValue(
          dataMap,
          dateKey,
          frame,
          AreaCode.CHUBU,
          DealPosition.OFFER,
        ),
        hokurikuBid: getKwhValue(
          dataMap,
          dateKey,
          frame,
          AreaCode.HOKURIKU,
          DealPosition.BID,
        ),
        hokurikuOffer: getKwhValue(
          dataMap,
          dateKey,
          frame,
          AreaCode.HOKURIKU,
          DealPosition.OFFER,
        ),
        kansaiBid: getKwhValue(
          dataMap,
          dateKey,
          frame,
          AreaCode.KANSAI,
          DealPosition.BID,
        ),
        kansaiOffer: getKwhValue(
          dataMap,
          dateKey,
          frame,
          AreaCode.KANSAI,
          DealPosition.OFFER,
        ),
        chugokuBid: getKwhValue(
          dataMap,
          dateKey,
          frame,
          AreaCode.CHUGOKU,
          DealPosition.BID,
        ),
        chugokuOffer: getKwhValue(
          dataMap,
          dateKey,
          frame,
          AreaCode.CHUGOKU,
          DealPosition.OFFER,
        ),
        shikokuBid: getKwhValue(
          dataMap,
          dateKey,
          frame,
          AreaCode.SHIKOKU,
          DealPosition.BID,
        ),
        shikokuOffer: getKwhValue(
          dataMap,
          dateKey,
          frame,
          AreaCode.SHIKOKU,
          DealPosition.OFFER,
        ),
        kyushuBid: getKwhValue(
          dataMap,
          dateKey,
          frame,
          AreaCode.KYUSHU,
          DealPosition.BID,
        ),
        kyushuOffer: getKwhValue(
          dataMap,
          dateKey,
          frame,
          AreaCode.KYUSHU,
          DealPosition.OFFER,
        ),
      }

      allRows.push(row)
    }
  }

  return allRows
}

/**
 * Calculate frame start time (assuming 30-minute intervals)
 */
function formatFrameStartTime(frame: number): string {
  const totalMinutes =
    (frame - FRAME_CONFIG.FRAME_START) * FRAME_CONFIG.FRAME_MINUTES
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`
}

/**
 * Calculate frame end time (assuming 30-minute intervals)
 */
function formatFrameEndTime(frame: number): string {
  const totalMinutes =
    (frame - FRAME_CONFIG.FRAME_START + 1) * FRAME_CONFIG.FRAME_MINUTES
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`
}

/**
 * Extract unique dates and create lookup map in single pass for efficiency
 * Structure: Map<dateKey, Map<frameNumber, Map<areaCode_dealPosition, kwhValue>>>
 */
function createDatesAndLookupMap(
  dealsPerFrame: DealPerFrame[],
  fromDate: Date,
  toDate: Date,
): {
  dates: Date[]
  dataMap: Map<string, Map<number, Map<string, number>>>
} {
  const dates: Date[] = []
  const targetDate = new Date(fromDate)
  while (targetDate <= toDate) {
    dates.push(new Date(targetDate))
    targetDate.setDate(targetDate.getDate() + 1)
  }

  const dealsByDate = new Map<string, DealPerFrame[]>()
  for (const deal of dealsPerFrame) {
    if (deal.date == null) {
      continue
    }
    const dateKey = format(
      new Date(deal.date.year, deal.date.month - 1, deal.date.day),
      'yyyy-MM-dd',
    )

    if (!dealsByDate.has(dateKey)) {
      dealsByDate.set(dateKey, [])
    }
    dealsByDate.get(dateKey)?.push(deal)
  }

  const dataMap = new Map<string, Map<number, Map<string, number>>>()

  for (const date of dates) {
    const dateKey = format(date, 'yyyy-MM-dd')

    const dateFrameMap = new Map<number, Map<string, number>>()
    dataMap.set(dateKey, dateFrameMap)

    const dealsForDate = dealsByDate.get(dateKey) ?? []
    for (const deal of dealsForDate) {
      for (const frameData of deal.frames) {
        let frameKwhMap = dateFrameMap.get(frameData.frame)
        if (frameKwhMap == null) {
          frameKwhMap = new Map()
          dateFrameMap.set(frameData.frame, frameKwhMap)
        }

        const areaPositionKey = `${deal.areaCode}_${deal.dealPosition}`
        const kwhValue = parseFloat(frameData.kwh?.value ?? '0')
        frameKwhMap.set(areaPositionKey, kwhValue)
      }
    }
  }

  return { dates, dataMap }
}

/**
 * Get kWh value from lookup map with O(1) access
 */
function getKwhValue(
  dataMap: Map<string, Map<number, Map<string, number>>>,
  dateKey: string,
  frame: number,
  areaCode: AreaCode,
  dealPosition: DealPosition,
): number {
  const dateFrameMap = dataMap.get(dateKey)
  if (dateFrameMap == null) {
    return 0
  }

  const frameKwhMap = dateFrameMap.get(frame)
  if (frameKwhMap == null) {
    return 0
  }

  const areaPositionKey = `${areaCode}_${dealPosition}`
  return frameKwhMap.get(areaPositionKey) ?? 0
}

/**
 * Generate CSV string for deal reports (convenience wrapper)
 */
export function generateDealReportCsvString(
  rows: DealReportCsvRow[],
  headerLabels: Record<keyof DealReportCsvRow, string>,
): string {
  return generateCsvString(rows, CSV_COLUMN_ORDER, headerLabels)
}

/**
 * Split CSV rows by area code
 * Returns a map of area names to filtered CSV rows containing only that area's data
 */
export function splitCsvRowsByArea(
  rows: DealReportCsvRow[],
  headerLabels: Record<keyof DealReportCsvRow, string>,
  fromDate: Date,
  toDate: Date,
): Map<string, { filename: string; content: string }> {
  const areaFiles = new Map<string, { filename: string; content: string }>()

  // Define area groups with their corresponding CSV columns
  const areaGroups = [
    {
      areaCode: AreaCode.HOKKAIDO,
      areaName: 'hokkaido',
      displayName: 'hokkaido',
      columns: ['hokkaidoBid', 'hokkaidoOffer'] as const,
    },
    {
      areaCode: AreaCode.TOHOKU,
      areaName: 'tohoku',
      displayName: 'tohoku',
      columns: ['tohokuBid', 'tohokuOffer'] as const,
    },
    {
      areaCode: AreaCode.TOKYO,
      areaName: 'tokyo',
      displayName: 'tokyo',
      columns: ['tokyoBid', 'tokyoOffer'] as const,
    },
    {
      areaCode: AreaCode.CHUBU,
      areaName: 'chubu',
      displayName: 'chubu',
      columns: ['chubuBid', 'chubuOffer'] as const,
    },
    {
      areaCode: AreaCode.HOKURIKU,
      areaName: 'hokuriku',
      displayName: 'hokuriku',
      columns: ['hokurikuBid', 'hokurikuOffer'] as const,
    },
    {
      areaCode: AreaCode.KANSAI,
      areaName: 'kansai',
      displayName: 'kansai',
      columns: ['kansaiBid', 'kansaiOffer'] as const,
    },
    {
      areaCode: AreaCode.CHUGOKU,
      areaName: 'chugoku',
      displayName: 'chugoku',
      columns: ['chugokuBid', 'chugokuOffer'] as const,
    },
    {
      areaCode: AreaCode.SHIKOKU,
      areaName: 'shikoku',
      displayName: 'shikoku',
      columns: ['shikokuBid', 'shikokuOffer'] as const,
    },
    {
      areaCode: AreaCode.KYUSHU,
      areaName: 'kyushu',
      displayName: 'kyushu',
      columns: ['kyushuBid', 'kyushuOffer'] as const,
    },
  ]

  for (const areaGroup of areaGroups) {
    // Define column order for this area (only common columns + this area's columns)
    const areaColumnOrder = [
      'date',
      'frame',
      'timeFrom',
      'timeTo',
      ...areaGroup.columns,
    ]

    // Create filtered header labels for this area
    const areaHeaderLabels: Record<string, string> = {}
    for (const col of areaColumnOrder) {
      const key = col as keyof DealReportCsvRow
      areaHeaderLabels[col] = headerLabels[key] ?? col
    }

    const filteredRows = rows.map((row) => {
      const areaRow: Record<string, string | number> = {
        date: row.date,
        frame: row.frame,
        timeFrom: row.timeFrom,
        timeTo: row.timeTo,
      }

      // Add only this area's columns
      for (const column of areaGroup.columns) {
        areaRow[column] = row[column]
      }

      return areaRow
    })

    // Generate CSV with only this area's columns
    const csvContent = generateCsvString(
      filteredRows,
      areaColumnOrder as Array<keyof DealReportCsvRow>,
      areaHeaderLabels,
    )
    const filename = generateContractExportFileName({
      fromDate,
      toDate,
      type: 'PER_FRAME',
      fileType: 'csv',
      areaName: areaGroup.areaName,
    })

    areaFiles.set(areaGroup.areaName, {
      filename,
      content: csvContent,
    })
  }

  return areaFiles
}
